import React, { useState, useContext, useEffect } from "react";

import {
  DataGrid,
  GridColDef,
  jaJP,
} from "@mui/x-data-grid";

import moment from "moment";

import JackUploadDialog from "./JackUploadDialog";
import JackDownloadConfirmDialog from "./JackDownloadConfirmDialog";
import ContentSaiseiDialog from "./ContentSaiseiDialog";

import { JACK_UPLOAD_STATUS } from "../../const/index";
import { ReloadContext } from "../../context/ReloadContext";
import { renderCellExpand } from "../Common/GridCellExpand";
import {
  KaizodoData, KaizodoDataSet
} from "../../types/WebData";
import { postSearchJackContent } from "../../api/jackContent";

/**
 * DataGrid用にデータを編集
 *
 * @param {KaizodoDataSet[]} kaizodoDatas
 * @return {*} 
 */
const makeGridData = (kaizodoDatas: KaizodoDataSet[]) => {
  return kaizodoDatas.map((kaizodoData) => {
    // 日付フォーマット
    const updateDate = moment(kaizodoData.update_dt);
    return {
      ...kaizodoData,
      update_dt: updateDate.format("YYYY/MM/DD"),
    };
  });
};

/**
 * 全件データを１ページ分の行にsliceしている
 * 値に単位をつけるなどのデータの編集も、ここで１ページ分のみ行う
 */
const loadServerRows = (
  page: number,
  pageSize: number,
  allRows: KaizodoDataSet[]
): Promise<KaizodoDataSet[]> =>
  new Promise<KaizodoDataSet[]>((resolve) => {
    if (!allRows) return;
    resolve(
      // １ページ分にsliceした後に、データを編集するfunctionに渡している
      makeGridData(allRows.slice(page * pageSize, (page + 1) * pageSize))
    );
  });

const useQuery = (page: number, pageSize: number, allRows: any[]) => {
  const [rowCount, setRowCount] = React.useState<number | undefined>(undefined);
  const [data, setData] = React.useState<KaizodoDataSet[]>([]);

  useEffect(() => {
    let active = true;

    setRowCount(undefined);
    loadServerRows(page, pageSize, allRows).then((newRows) => {
      if (!active) {
        return;
      }
      setData(newRows);
      setRowCount(allRows.length);
    });

    return () => {
      active = false;
    };
  }, [page, pageSize, allRows]);

  return { data, rowCount };
};

interface RowsState {
  page: number;
  pageSize: number;
}

type Props = {
  jackId: number;
};

/**
 * ジャック詳細一覧コンポーネント
 */
export const JackShosaiGrid = ({ jackId }: Props) => {
  const reloadContext = useContext(ReloadContext);
  const [kaizodoData, setKaizodoData] = useState<KaizodoData>({ data: [] });

  useEffect(() => {
    const getSearchData = async () => {
      const result = await postSearchJackContent(jackId);
      setKaizodoData(result);
      if (result?.isError) {
        await reloadContext?.setSnackbarInfo({
          isOpen: true,
          type: "error",
          message: result.message,
        });
      }
    };

    getSearchData();
  }, [reloadContext?.reload]);

  const columns: GridColDef[] =
    [
      {
        field: "id",
        headerName: "ID",
        hide: true,
        filterable: false,
        disableColumnMenu: true,
      },
      {
        field: "kaizodo",
        headerName: "解像度",
        width: 200,
        sortable: false,
        disableColumnMenu: true,
        renderCell: renderCellExpand,
      },
      {
        field: "content_file_mei",
        headerName: "コンテンツファイル名",
        width: 200,
        sortable: false,
        disableColumnMenu: true,
        cellClassName: (params) => {
          let className = "";
          className += params.row.upload_status === JACK_UPLOAD_STATUS.UPLOAD_FAILED ? "upload-failed " : "";
          return className;
        },
        renderCell: renderCellExpand,
      },
      {
        field: "update_dt",
        headerName: "更新日",
        width: 100,
        sortable: false,
        disableColumnMenu: true,
      },
      {
        field: 'uploadButton',
        headerName: "",
        width: 20,
        align: "center",
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <JackUploadDialog
            contentId={params.row.id}
            contentFileMei={params.row.content_file_mei}
            contentByosu={params.row.content_byosu}
            jackId={params.row.jack_id}
            kaizodoId={params.row.kaizodo_id}
          />
        )
      },
      {
        field: "downloadButton",
        headerName: "",
        width: 20,
        align: "center",
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <JackDownloadConfirmDialog
            jackContentId={params.row.id}
            contentFilePath={params.row.content_file_path}
            contentFileMei={params.row.content_file_mei}
          />
        ),
      },
      {
        field: 'saiseiButton',
        headerName: "",
        width: 20,
        align: "center",
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <ContentSaiseiDialog
            contentId={params.row.id}
            bangumiMei={params.row.bangumi_mei}
            contentFileMei={params.row.content_file_mei}
            contentFilePath={params.row.content_file_path}
            contentByosu={params.row.content_byosu}
            contentFileExtension={params.row.content_file_extension}
            kaizodo={params.row.kaizodo}
          />
        )
      },
    ];

  // ページのstate　１ページに１０行
  const [rowsState, setRowsState] = React.useState<RowsState>({
    page: 0,
    pageSize: 10,
  });

  const { data, rowCount } = useQuery(
    rowsState.page,
    rowsState.pageSize,
    kaizodoData.data
  );

  // データの読み込みが間に合ってなくてrowCountがundefinedのままの場合の対策
  const [rowCountState, setRowCountState] = useState(rowCount || 0);
  useEffect(() => {
    setRowCountState((prevRowCountState) =>
      rowCount !== undefined ? rowCount : prevRowCountState
    );
  }, [rowCount, setRowCountState]);

  return (
    <div style={{ height: 650, width: "100%" }}>
      <DataGrid
        rows={data}
        columns={columns}
        rowCount={rowCountState}
        {...rowsState}
        rowsPerPageOptions={[10]}
        pagination
        paginationMode="server"
        sortModel={[{ field: "kaizodo_id", sort: "asc" }]}
        sx={{ '.upload-failed': { color: "red", textDecoration: "line-through" } }}
        onPageChange={(page) => setRowsState((prev) => ({ ...prev, page }))}
        onPageSizeChange={(pageSize) =>
          setRowsState((prev) => ({ ...prev, pageSize }))
        }
        localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
      />
    </div>
  );
};